// import type { paths } from './api.generated  ';
import { showErrorNotification } from '@vrplatform/theme';
import {
  showWarnNotification,
  updateErrorNotification,
} from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';
import {
  type InfiniteData,
  type QueryKey as TanstackQueryKey,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  useInfiniteQuery as useTanstackInfiniteQuery,
  useMutation,
  useQuery as useTanstackQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { ApiClientError, paths } from '@vrplatform/api';
import type { HasuraClient } from '@vrplatform/graphql';
import createFetchClient, {
  type FetchResponse,
  type MaybeOptionalInit,
  type Middleware,
} from 'openapi-fetch';
import createClient from 'openapi-react-query';
import type {
  HttpMethod,
  MediaType,
  PathsWithMethod,
} from 'openapi-typescript-helpers';
import {
  isGatewayTimeoutError,
  MAX_GATEWAY_TIMEOUT_RETRIES,
} from './api.retry';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { PLATFORM } from '../env';
import { type QueryKey, useGqtyClient } from '../graphql';
import {
  captureSentryError,
  type PollUntilCompletedProps,
  useMe,
  usePollUntilCompleted,
  useTeamRole,
} from '../hooks';
import { isStorybook } from '../env';
import {
  useLocalStorage,
  useNetwork,
  readLocalStorageValue,
} from '@mantine/hooks';
import { getTeamScopedPlaceholderData } from '../utils';
import {
  getAuditActionNotificationStorageKey,
  showAuditActionNotification,
} from '../notification';
import type {
  ClientMethodForPaths,
  InitParam,
  InitWithUnknowns,
  MethodForPath,
} from './api.types';

const API_ENDPOINT_STORAGE_KEY = 'api-endpoint';

const getDefaultApiHost = () => {
  if (typeof window === 'undefined') return 'api.vrplatform.app';
  const hostname = window.location.hostname;

  if (
    hostname === 'localhost' ||
    hostname === 'staging.portal.vrplatform.app' ||
    hostname.includes('app-256.pages.dev')
  ) {
    return 'staging.api.vrplatform.app';
  }

  return 'api.vrplatform.app';
};

const normalizeApiHost = (value?: string | null) => {
  if (!value) return undefined;

  const trimmed = value.replaceAll('"', '').trim();
  if (!trimmed) return undefined;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      return new URL(trimmed).host;
    } catch {
      return trimmed;
    }
  }

  return trimmed;
};

const resolveApiHost = (value?: string | null) =>
  normalizeApiHost(value) ?? getDefaultApiHost();

const getApiBaseUrl = (host: string) => {
  const protocol = host.includes('localhost') ? 'http' : 'https';
  return host.startsWith('http://') || host.startsWith('https://')
    ? host
    : `${protocol}://${host}`;
};

export type api = paths;

export { ApiClientError } from '@vrplatform/api';

type ApiContext = Client & {
  teamId: string | null | undefined;
  isTeamIdFetching: boolean;
  isTeamSwitching: boolean;
  apiHost: string;
  baseUrl: string;
};

const context = createContext<ApiContext>({
  queryOptions: undefined,
  useMutation: undefined,
  useQuery: undefined,
  useSuspenseQuery: undefined,
  apiHost: '',
  baseUrl: '',
  teamId: undefined,
  isTeamSwitching: false,
} as any);

type Client = ReturnType<typeof createClient<paths>> & {
  $api: ReturnType<typeof createFetchClient<paths>>;
};

export const QUERY_GROUPS = [
  'accounts',
  'apps',
  'automations',
  'bank-records',
  'bank-rules',
  'bank-transfers',
  'connections',
  'deposits',
  'expenses',
  'fees',
  'listings',
  'owners',
  'reports',
  'reservations',
  'owner-statements',
  'pm-statements',
  'teams',
  'settings',
] as const;

export type QueryGroup = (typeof QUERY_GROUPS)[number];

type ApiPaths = paths;

type ApiMethodForPath<
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
> = MethodForPath<ApiPaths, Method, Path>;

type ApiOperation<
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
> = NonNullable<ApiPaths[Path][Method]>;

type ApiSuccessResponse<
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
  Init extends MaybeOptionalInit<
    ApiPaths[Path],
    ApiMethodForPath<Method, Path>
  >,
  Media extends MediaType = MediaType,
> = Required<
  Extract<FetchResponse<ApiOperation<Method, Path>, Init, Media>, { data: any }>
>;

type ApiErrorResponse<
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
  Init extends MaybeOptionalInit<
    ApiPaths[Path],
    ApiMethodForPath<Method, Path>
  >,
  Media extends MediaType = MediaType,
> = Extract<
  FetchResponse<ApiOperation<Method, Path>, Init, Media>,
  { error: any }
>['error'];

export type UseMutationWithResponseMethod<
  Paths extends Record<string, Record<HttpMethod, {}>>,
  Media extends MediaType,
> = <
  Method extends HttpMethod,
  Path extends PathsWithMethod<Paths, Method>,
  Init extends MaybeOptionalInit<Paths[Path], Method>,
  Response extends Required<
    Extract<
      FetchResponse<NonNullable<Paths[Path][Method]>, Init, Media>,
      {
        data: any;
      }
    >
  >,
  Error extends Extract<
    FetchResponse<NonNullable<Paths[Path][Method]>, Init, Media>,
    { error: any }
  >['error'],
  Options extends Omit<
    UseMutationOptions<Response, Error, Init>,
    'mutationKey' | 'mutationFn'
  >,
>(
  method: Method,
  url: Path,
  options?: Options,
  queryClient?: QueryClient
) => UseMutationResult<Response, Error, Init>;

export const useApiEndpoint = () => {
  const [storedApiHost, setStoredApiHost, removeStoredApiHost] =
    useLocalStorage<string | undefined>({
      key: API_ENDPOINT_STORAGE_KEY,
      getInitialValueInEffect: false,
    });

  const apiHost = useMemo(() => resolveApiHost(storedApiHost), [storedApiHost]);
  const baseUrl = useMemo(() => getApiBaseUrl(apiHost), [apiHost]);

  const setApiHost = useCallback(
    (value?: string) => {
      if (!value) {
        removeStoredApiHost();
        return;
      }
      setStoredApiHost(value);
    },
    [removeStoredApiHost, setStoredApiHost]
  );

  return { apiHost, baseUrl, setApiHost, storedApiHost };
};

export const getApiErrorMessage = (
  error: ApiClientError,
  defaultMessage: string
) =>
  error?.issues?.[0]?.message ||
  error?.error?.message ||
  error?.error?.[0]?.message ||
  error?.message ||
  defaultMessage;

export const showApiErrorNotification = ({
  error,
  title,
  defaultMessage = 'Please try again later and contact support if the issue persists.',
  id,
}: {
  error: any;
  title: string;
  defaultMessage?: string;
  id?: string;
}) => {
  const message = getApiErrorMessage(error, defaultMessage);

  if (id) {
    updateErrorNotification({
      id,
      title,
      message,
    });
  } else {
    showErrorNotification({
      title,
      message,
    });
  }
};

export const ApiClientProvider = ({
  allowOpaqueAccessToken = false,
  baseUrlOverride,
  children,
  getToken,
  teamId,
  isTeamIdFetching,
  isTeamSwitching,
}: {
  allowOpaqueAccessToken?: boolean;
  baseUrlOverride?: string | null;
  children: React.ReactNode;
  getToken: () => Promise<Maybe<string>>;
  teamId: string | null | undefined;
  isTeamIdFetching: boolean;
  isTeamSwitching: boolean;
}) => {
  const endpoint = useApiEndpoint();
  const apiHost = useMemo(
    () =>
      baseUrlOverride
        ? (normalizeApiHost(baseUrlOverride) ?? endpoint.apiHost)
        : endpoint.apiHost,
    [baseUrlOverride, endpoint.apiHost]
  );
  const baseUrl = useMemo(
    () => (baseUrlOverride ? getApiBaseUrl(baseUrlOverride) : endpoint.baseUrl),
    [baseUrlOverride, endpoint.baseUrl]
  );
  const queryClient = useQueryClient();
  const previousApiHost = useRef<string | null>(null);

  useEffect(() => {
    if (previousApiHost.current === null) {
      previousApiHost.current = apiHost;
      return;
    }

    if (previousApiHost.current === apiHost) return;

    previousApiHost.current = apiHost;
    queryClient.invalidateQueries({ refetchType: 'active' });
  }, [apiHost, queryClient]);

  const { $api, fetchClient } = useMemo(() => {
    const authMiddleware: Middleware = {
      async onRequest({ request }) {
        // const accessToken = await auth.session?.getToken({
        //   template: 'Hasura',
        // });

        const accessToken = await getToken();
        const normalizedAccessToken =
          typeof accessToken === 'string' ? accessToken.trim() : '';

        const hasUsableAccessToken =
          !!normalizedAccessToken &&
          normalizedAccessToken !== 'undefined' &&
          normalizedAccessToken !== 'null';

        if (!hasUsableAccessToken) {
          throw new Error('No API access');
        }

        const hasJwtShape = normalizedAccessToken.split('.').length === 3;

        // PRO-12793: keep the existing malformed-token guard for the normal
        // app path, while allowing opaque server-issued embed sessions.
        if (!allowOpaqueAccessToken && !hasJwtShape) {
          throw new Error('No API access');
        }

        const existingHeaderTenantId = request.headers.get('x-team-id');
        const headerTenantId = existingHeaderTenantId || teamId;

        // add Authorization header to every request
        request.headers.set('Authorization', `Bearer ${normalizedAccessToken}`);
        if (headerTenantId) request.headers.set('x-team-id', headerTenantId);
        request.headers.set(
          'x-transaction-id',
          Math.random().toString(36).substring(2, 9)
        );
        request.headers.set('platform', PLATFORM);

        return request;
      },
    };

    const fetchClient = createFetchClient<paths>({
      baseUrl,
    });

    fetchClient.use(authMiddleware);

    const $api = createClient(fetchClient);
    return { $api, fetchClient };
  }, [allowOpaqueAccessToken, teamId, getToken, baseUrl]);

  const value = useMemo(
    () => ({
      ...$api,
      $api: fetchClient,
      apiHost,
      baseUrl,
      teamId,
      isTeamIdFetching,
      isTeamSwitching,
    }),
    [
      $api,
      fetchClient,
      apiHost,
      baseUrl,
      teamId,
      isTeamIdFetching,
      isTeamSwitching,
    ]
  );

  return <context.Provider value={value}>{children}</context.Provider>;
};
export const useApiMutation = <
  Method extends HttpMethod,
  Path extends PathsWithMethod<ApiPaths, Method>,
  Init extends MaybeOptionalInit<
    ApiPaths[Path],
    ApiMethodForPath<Method, Path>
  >,
  Options extends Omit<
    UseMutationOptions<
      ApiSuccessResponse<Method, Path, Init>,
      ApiErrorResponse<Method, Path, Init>,
      Init
    >,
    'mutationKey' | 'mutationFn'
  >,
>(
  method: Method,
  path: Path,
  options?: Options,
  queryClient?: QueryClient
) => {
  const api = useContext(context).$api;
  const { isVrpAdmin } = useTeamRole();
  const { id: meId } = useMe();
  const { onSuccess: userOnSuccess, ...restOptions } = options ?? {};

  return useMutation<
    ApiSuccessResponse<Method, Path, Init>,
    ApiErrorResponse<Method, Path, Init>,
    Init
  >(
    {
      mutationKey: [method, path, 'response'],
      mutationFn: async (init) => {
        const mth = method.toUpperCase() as Uppercase<typeof method>;
        const fn = api[mth] as ClientMethodForPaths<
          ApiPaths,
          typeof method,
          MediaType
        >;
        const initParam = (
          init === undefined ? [] : [init as InitWithUnknowns<typeof init>]
        ) as InitParam<Init>;
        const res = await fn<Path, Init>(path, ...initParam);

        if (res.error) {
          throw res.error;
        }

        return res as ApiSuccessResponse<Method, Path, Init>;
      },
      ...restOptions,
      onSuccess: (data, variables, onMutateResult, context) => {
        const isActionNotificationEnabled = readLocalStorageValue<
          boolean | undefined
        >({
          key: getAuditActionNotificationStorageKey(meId),
        });

        if (isVrpAdmin && isActionNotificationEnabled) {
          showAuditActionNotification({
            response: data.response,
          });
        }

        userOnSuccess?.(data, variables, onMutateResult, context);
      },
    },
    queryClient
  );
};

export const useApiClient = () => useContext(context).$api;
export const useApiHost = () => useContext(context).apiHost;
export const useApiTeamId = () => useContext(context).teamId;
export const useApiBaseUrl = () => useContext(context).baseUrl;
export const useApiIsTeamIdFetching = () =>
  useContext(context).isTeamIdFetching;
export const useApiIsTeamSwitching = () => useContext(context).isTeamSwitching;
export const uesApiMutationBase: Client['useMutation'] = (...props) =>
  useContext(context).useMutation(...props);

export type ApiClient = ReturnType<typeof useApiClient>;

class ApiQueryError extends Error {
  constructor(readonly message: string) {
    super(message);
    this.name = 'ApiQueryError';
  }
}

function handleQueryError(
  error: ApiClientError,
  signal: AbortSignal,
  opts: {
    hideNotification: boolean;
    title?: string;
  },
  extra: Record<string, string | boolean | undefined>
) {
  const title = opts.title || 'Query Error';
  const message = error?.message;

  const errorCode = error.code?.toLowerCase();
  const isNotFound = errorCode === 'not_found';
  const isServerError =
    errorCode === 'service_unavailable' ||
    errorCode === 'internal_server_error';

  if (!message || signal?.aborted || isNotFound) return;

  const lower = message.toLowerCase();
  const isMalformedJwtMessage =
    lower.includes('could not verify jwt') ||
    lower.includes('compactdecodeerror') ||
    lower.includes('invalid number of parts');

  const disableNotification =
    lower.includes('failed to fetch') ||
    lower.includes('load failed') ||
    lower.includes('database query error') ||
    lower.includes('no api access') ||
    isMalformedJwtMessage;

  const isLocalhostApi =
    typeof extra.apiHost === 'string' && extra.apiHost.includes('localhost');

  const disableSentry =
    lower.includes('load failed') ||
    lower.includes('signal is aborted without reason') ||
    isLocalhostApi ||
    isServerError ||
    isMalformedJwtMessage;

  if (!disableSentry)
    captureSentryError(new ApiQueryError(message), {
      fingerprint: [
        '{{ default }}',
        'api-query',
        error.code || 'unknown-code',
        message,
      ],
      level: 'error',
      tags: {
        api_error_code: error.code || 'unknown-code',
        error_source: 'api-query',
      },
      extra: {
        ...extra,
        apiError: error,
      },
    });

  if (opts.hideNotification || disableNotification) {
    // do not show notification to user
    return;
  }

  if (lower.includes('exceeded the time limit of 60s')) {
    showWarnNotification({
      title: 'Connection Timeout',
      message: `Due to a possible slow internet connection, or temporarily high load on our servers, the connection from your computer has timed out. Please try again later.`,
    });
    return;
  }

  showApiErrorNotification({
    error,
    title: title,
    defaultMessage:
      'We failed to fetch your data. Please try again later and contact support if the issue persists. ',
  });
}

export function useApiQuery<TData, TArgs extends Record<string, unknown>>(
  fn: (
    api: ApiClient,
    signal: AbortSignal,
    variables: TArgs,
    hasura: HasuraClient['query']
  ) => Promise<TData>,
  options: {
    skip?: boolean;
    variables: TArgs;
    queryGroup: QueryGroup;
    uniqueQueryKey: string;
    queryKeys?: QueryKey[];
    keepPreviousData?: boolean;
    errorTitle?: string;
    hideNotification?: boolean;
    staleTime?: number;
    storybookQueryKey?: string;
    refetchOnMount?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnWindowFocus?: boolean;
    gcTime?: number;
    allowDuringTeamSwitch?: boolean;
    poll?: Omit<
      PollUntilCompletedProps,
      'refetchQuery' | 'skip' | 'isCompleted'
    > & {
      isCompleted: (data: TData | undefined) => boolean;
    };
  }
) {
  const { online: isOnline } = useNetwork();

  const $api = useApiClient();
  const apiHost = useApiHost();
  const $graphql = useGqtyClient();

  const teamId = useApiTeamId();
  const isFetchingTeam = useApiIsTeamIdFetching();
  const isTeamSwitching = useApiIsTeamSwitching();

  const queryKey = useMemo(
    () => [
      options.queryGroup,
      options.uniqueQueryKey,
      apiHost,
      options.variables,
      options.storybookQueryKey,
      ...(options.queryKeys || []),
      options.hideNotification,
      teamId,
    ],
    [
      options.queryGroup,
      options.uniqueQueryKey,
      apiHost,
      options.variables,
      options.storybookQueryKey,
      options.queryKeys,
      teamId,
      options.hideNotification,
    ]
  );

  const shouldGate =
    isFetchingTeam || (isTeamSwitching && !options.allowDuringTeamSwitch);

  const queryData = useTanstackQuery<TData, ApiClientError>({
    queryKey,
    placeholderData: options.keepPreviousData
      ? getTeamScopedPlaceholderData(teamId)
      : undefined,
    staleTime:
      options.staleTime ?? (options.keepPreviousData ? 30_000 : undefined),
    gcTime: options.gcTime,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    refetchOnReconnect: options.refetchOnReconnect,
    refetchOnMount: options.refetchOnMount,
    enabled: !shouldGate && !options.skip,
    retry: (failureCount, error) =>
      isGatewayTimeoutError(error) &&
      failureCount < MAX_GATEWAY_TIMEOUT_RETRIES,
    queryFn: ({ signal }) => {
      if (isStorybook && options.storybookQueryKey) {
        return fetch(
          `${window.location.origin}/storybook/${options.storybookQueryKey}`,
          { signal }
        ).then((res) => res.json() as Promise<TData>);
      }

      return fn($api, signal, options.variables, $graphql.query).catch(
        (error: ApiClientError) => {
          handleQueryError(
            error,
            signal,
            {
              hideNotification: !!options.hideNotification,
              title: options.errorTitle,
            },
            {
              isOnline,
              query: fn?.toString(),
              apiHost,
            }
          );
          throw error;
        }
      );
    },
  });

  const isCompleted = useMemo(() => {
    return !!options?.poll?.isCompleted(queryData.data);
  }, [queryData.data, options?.poll?.isCompleted]);

  usePollUntilCompleted(
    options?.poll
      ? {
          isCompleted,
          skip: options.skip ?? false,
          interval: options.poll.interval,
          onCompleted: options.poll.onCompleted,
          refetchQuery: queryData.refetch,
        }
      : undefined
  );

  return { ...queryData, queryKey };
}

export function useInfiniteApiQuery<
  TData,
  TArgs extends Record<string, unknown>,
  TPageParam = number,
>(
  fn: (
    api: ApiClient,
    signal: AbortSignal,
    variables: TArgs,
    pageParam: TPageParam,
    hasura: HasuraClient['query']
  ) => Promise<TData>,
  options: {
    skip?: boolean;
    variables: TArgs;
    queryGroup: QueryGroup;
    uniqueQueryKey: string;
    queryKeys?: QueryKey[];
    errorTitle?: string;
    hideNotification?: boolean;
    refetchOnMount?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    gcTime?: number;
    storybookQueryKey?: string;
    initialPageParam: TPageParam;
    allowDuringTeamSwitch?: boolean;
    getNextPageParam: (
      lastPage: TData,
      allPages: TData[],
      lastPageParam: TPageParam,
      allPageParams: TPageParam[]
    ) => TPageParam | undefined | null;
    getPreviousPageParam?: (
      firstPage: TData,
      allPages: TData[],
      firstPageParam: TPageParam,
      allPageParams: TPageParam[]
    ) => TPageParam | undefined | null;
    maxPages?: number;
    keepPreviousData?: boolean;
  }
) {
  const { online: isOnline } = useNetwork();

  const $api = useApiClient();
  const apiHost = useApiHost();
  const $graphql = useGqtyClient();

  const teamId = useApiTeamId();
  const isFetchingTeam = useApiIsTeamIdFetching();
  const isTeamSwitching = useApiIsTeamSwitching();

  const queryKey = useMemo(
    () => [
      options.queryGroup,
      options.uniqueQueryKey,
      apiHost,
      options.variables,
      options.storybookQueryKey,
      ...(options.queryKeys || []),
      options.hideNotification,
      teamId,
    ],
    [
      options.queryGroup,
      options.uniqueQueryKey,
      apiHost,
      options.variables,
      options.storybookQueryKey,
      options.queryKeys,
      teamId,
      options.hideNotification,
    ]
  );

  const shouldGate =
    isFetchingTeam || (isTeamSwitching && !options.allowDuringTeamSwitch);

  const queryData = useTanstackInfiniteQuery<
    TData,
    ApiClientError,
    InfiniteData<TData>,
    TanstackQueryKey,
    TPageParam
  >({
    queryKey,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    refetchOnReconnect: options.refetchOnReconnect,
    staleTime: options.staleTime,
    gcTime: options.gcTime,
    enabled: !!teamId && !shouldGate && !options.skip,
    initialPageParam: options.initialPageParam,
    getNextPageParam: options.getNextPageParam,
    getPreviousPageParam: options.getPreviousPageParam,
    refetchOnMount: options.refetchOnMount ?? true,
    maxPages: options.maxPages,
    placeholderData: getTeamScopedPlaceholderData(teamId),
    queryFn: ({ signal, pageParam }) => {
      if (isStorybook && options.storybookQueryKey) {
        return fetch(
          `${window.location.origin}/storybook/${options.storybookQueryKey}`,
          { signal, method: 'GET' }
        ).then((res) => res.json() as Promise<TData>);
      }

      return fn(
        $api,
        signal,
        options.variables,
        pageParam as TPageParam,
        $graphql.query
      ).catch((error: ApiClientError) => {
        handleQueryError(
          error,
          signal,
          {
            hideNotification: !!options.hideNotification,
            title: options.errorTitle,
          },
          {
            isOnline,
            query: fn?.toString(),
            apiHost,
          }
        );
        throw error;
      });
    },
  });

  return { ...queryData, queryKey };
}
